import { useState, useEffect, useCallback } from 'react'
import { useJMAPAuth } from '../hooks/useJMAPAuth'
import { JmapMailClient, type Mailbox, type Email } from '../lib/jmap-mail'

type View = 'inbox' | 'compose' | 'read'

export default function Mail() {
  const { auth, login } = useJMAPAuth()
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([])
  const [selectedMailbox, setSelectedMailbox] = useState<string>('')
  const [emails, setEmails] = useState<Email[]>([])
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null)
  const [view, setView] = useState<View>('inbox')
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [loginEmail, setLoginEmail] = useState('')
  const [loginPassword, setLoginPassword] = useState('')
  const [loginError, setLoginError] = useState('')
  const [loggingIn, setLoggingIn] = useState(false)
  const limit = 20

  const client = auth ? new JmapMailClient(auth) : null

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoggingIn(true)
    setLoginError('')
    try {
      const ok = await login(loginEmail, loginPassword)
      if (!ok) setLoginError('Invalid credentials')
    } catch {
      setLoginError('Login failed')
    } finally {
      setLoggingIn(false)
    }
  }

  const loadMailboxes = useCallback(async () => {
    if (!client) {
      setLoading(false)
      return
    }
    try {
      const boxes = await client.getMailboxes()
      setMailboxes(boxes)
      const inbox = boxes.find((b) => b.role === 'inbox')
      if (inbox) setSelectedMailbox(inbox.id)
    } catch (err) {
      console.error('Failed to load mailboxes:', err)
    } finally {
      setLoading(false)
    }
  }, [client])

  const loadEmails = useCallback(async (mailboxId: string, position: number) => {
    if (!client || !mailboxId) return
    setLoading(true)
    try {
      const result = await client.getEmails(mailboxId, {
        limit,
        position,
        sort: 'receivedAt',
        isAscending: false,
      })
      setEmails(result.emails)
      setTotal(result.total)
    } catch (err) {
      console.error('Failed to load emails:', err)
    } finally {
      setLoading(false)
    }
  }, [client])

  useEffect(() => {
    loadMailboxes()
  }, [loadMailboxes])

  useEffect(() => {
    if (selectedMailbox) {
      loadEmails(selectedMailbox, page * limit)
    }
  }, [selectedMailbox, page, loadEmails])

  const openEmail = async (email: Email) => {
    if (!client) return
    try {
      const full = await client.getEmail(email.id, {
        fetchTextBody: true,
        fetchHtmlBody: true,
      })
      if (full) {
        setSelectedEmail(full)
        setView('read')
        // Mark as read
        if (!email.keywords['$seen']) {
          await client.markAsRead([email.id])
        }
      }
    } catch (err) {
      console.error('Failed to open email:', err)
    }
  }

  const getMailboxName = (mailboxId: string) => {
    const mailbox = mailboxes.find((b) => b.id === mailboxId)
    return mailbox?.name || 'Unknown'
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const isToday = date.toDateString() === now.toDateString()
    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
  }

  const getBodyPreview = (email: Email) => {
    const preview = email.preview || ''
    return preview.length > 100 ? preview.substring(0, 100) + '...' : preview
  }

  if (!auth) {
    return (
      <div className="h-full flex items-center justify-center">
        <form onSubmit={handleLogin} className="w-80 space-y-4">
          <h2 className="text-xl font-semibold text-[var(--color-text)]">Sign in to Mail</h2>
          {loginError && (
            <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
              {loginError}
            </div>
          )}
          <input
            type="email"
            value={loginEmail}
            onChange={(e) => setLoginEmail(e.target.value)}
            placeholder="user@envelo.me"
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            autoFocus
          />
          <input
            type="password"
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            placeholder="Password"
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
          />
          <button
            type="submit"
            disabled={loggingIn || !loginEmail || !loginPassword}
            className="w-full bg-[var(--color-primary)] text-white py-2 rounded-lg font-medium hover:opacity-90 disabled:opacity-50"
          >
            {loggingIn ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
    )
  }

  return (
    <div className="h-full flex">
      {/* Mailbox sidebar */}
      <div className="w-56 border-r border-[var(--color-border)] bg-[var(--color-bg-elevated)] flex flex-col">
        <div className="p-4 border-b border-[var(--color-border)]">
          <button
            onClick={() => { setView('compose'); setSelectedEmail(null) }}
            className="w-full bg-[var(--color-primary)] text-white py-2 px-4 rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            Compose
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto p-2">
          {mailboxes.map((mailbox) => (
            <button
              key={mailbox.id}
              onClick={() => {
                setSelectedMailbox(mailbox.id)
                setPage(0)
                setView('inbox')
              }}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                selectedMailbox === mailbox.id
                  ? 'bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                  : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="truncate">{mailbox.name}</span>
                {mailbox.unreadEmails > 0 && (
                  <span className="ml-2 bg-[var(--color-primary)] text-white text-xs px-1.5 py-0.5 rounded-full">
                    {mailbox.unreadEmails}
                  </span>
                )}
              </div>
            </button>
          ))}
        </nav>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {view === 'compose' && auth ? (
          <ComposeView
            auth={auth}
            mailboxes={mailboxes}
            selectedMailbox={selectedMailbox}
            onSent={() => { setView('inbox'); loadEmails(selectedMailbox, page * limit) }}
            onCancel={() => setView('inbox')}
          />
        ) : view === 'read' && selectedEmail ? (
          <ReadView
            email={selectedEmail}
            getMailboxName={getMailboxName}
            formatDate={formatDate}
            onBack={() => { setView('inbox'); setSelectedEmail(null) }}
          />
        ) : (
          <div className="flex-1 flex flex-col">
            {/* Email list header */}
            <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center justify-between">
              <h2 className="font-semibold text-[var(--color-text)]">
                {getMailboxName(selectedMailbox) || 'Mail'}
              </h2>
              <span className="text-sm text-[var(--color-text-muted)]">
                {total} {total === 1 ? 'message' : 'messages'}
              </span>
            </div>

            {/* Email list */}
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-[var(--color-text-muted)]">Loading...</div>
              </div>
            ) : emails.length === 0 ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-4xl mb-4">📭</div>
                  <div className="text-[var(--color-text-muted)]">No messages</div>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                {emails.map((email) => (
                  <button
                    key={email.id}
                    onClick={() => openEmail(email)}
                    className={`w-full text-left px-4 py-3 border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)] transition-colors ${
                      !email.keywords['$seen'] ? 'bg-[var(--color-primary-light)]' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <span className={`font-medium text-sm ${
                        !email.keywords['$seen'] ? 'text-[var(--color-text)]' : 'text-[var(--color-text-secondary)]'
                      }`}>
                        {email.from?.[0]?.name || email.from?.[0]?.email || 'Unknown'}
                      </span>
                      <span className="text-xs text-[var(--color-text-muted)]">
                        {formatDate(email.receivedAt)}
                      </span>
                    </div>
                    <div className={`text-sm ${
                      !email.keywords['$seen'] ? 'font-semibold text-[var(--color-text)]' : 'text-[var(--color-text-secondary)]'
                    }`}>
                      {email.subject || '(no subject)'}
                    </div>
                    <div className="text-xs text-[var(--color-text-muted)] mt-1 truncate">
                      {getBodyPreview(email)}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Pagination */}
            {total > limit && (
              <div className="px-4 py-3 border-t border-[var(--color-border)] flex items-center justify-between">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1 text-sm rounded border border-[var(--color-border)] disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-[var(--color-text-muted)]">
                  {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}
                </span>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={(page + 1) * limit >= total}
                  className="px-3 py-1 text-sm rounded border border-[var(--color-border)] disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function ComposeView({ auth, selectedMailbox, onSent, onCancel }: {
  auth: { username: string; password: string }
  mailboxes: Mailbox[]
  selectedMailbox: string
  onSent: () => void
  onCancel: () => void
}) {
  const client = new JmapMailClient(auth)
  const [to, setTo] = useState('')
  const [cc, setCc] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [sending, setSending] = useState(false)
  const [showCc, setShowCc] = useState(false)

  const handleSend = async () => {
    if (!to.trim()) return
    setSending(true)
    try {
      const toAddresses = to.split(',').map((email) => ({ email: email.trim() }))
      const ccAddresses = cc.trim() ? cc.split(',').map((email) => ({ email: email.trim() })) : undefined

      await client.submitEmail({
        mailboxId: selectedMailbox,
        from: [{ email: auth.username }],
        to: toAddresses,
        cc: ccAddresses,
        subject: subject || '(no subject)',
        textBody: body,
      })
      onSent()
    } catch (err) {
      console.error('Failed to send email:', err)
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="flex-1 flex flex-col">
      <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center justify-between">
        <h2 className="font-semibold text-[var(--color-text)]">New Message</h2>
        <button
          onClick={onCancel}
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] text-sm"
        >
          Cancel
        </button>
      </div>

      <div className="flex-1 flex flex-col p-4">
        <div className="space-y-3 mb-4">
          <div className="flex items-center gap-2">
            <label className="w-16 text-sm text-[var(--color-text-secondary)]">To</label>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="recipient@example.com"
              className="flex-1 px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            />
            {!showCc && (
              <button
                onClick={() => setShowCc(true)}
                className="text-sm text-[var(--color-primary)] hover:underline"
              >
                Cc
              </button>
            )}
          </div>

          {showCc && (
            <div className="flex items-center gap-2">
              <label className="w-16 text-sm text-[var(--color-text-secondary)]">Cc</label>
              <input
                type="email"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="cc@example.com"
                className="flex-1 px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              />
            </div>
          )}

          <div className="flex items-center gap-2">
            <label className="w-16 text-sm text-[var(--color-text-secondary)]">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              className="flex-1 px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            />
          </div>
        </div>

        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="flex-1 resize-none border border-[var(--color-border)] rounded-lg bg-[var(--color-bg)] text-[var(--color-text)] p-4 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
          placeholder="Write your message..."
        />

        <div className="flex items-center justify-end gap-2 mt-4">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm border border-[var(--color-border)] rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || !to.trim()}
            className="px-4 py-2 text-sm bg-[var(--color-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
          >
            {sending ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  )
}

function ReadView({ email, getMailboxName, formatDate, onBack }: {
  email: Email
  getMailboxName: (id: string) => string
  formatDate: (date: string) => string
  onBack: () => void
}) {
  const mailboxId = Object.keys(email.mailboxIds || {}).find((id) => email.mailboxIds[id])
  const mailboxName = mailboxId ? getMailboxName(mailboxId) : ''

  const bodyHtml = email.htmlBody || ''
  const bodyText = email.textBody || ''
  const hasBody = bodyHtml || bodyText

  return (
    <div className="flex-1 flex flex-col">
      <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center gap-3">
        <button
          onClick={onBack}
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        >
          ← Back
        </button>
        <div className="flex-1">
          <h2 className="font-semibold text-[var(--color-text)]">{email.subject || '(no subject)'}</h2>
          {mailboxName && (
            <span className="text-xs text-[var(--color-text-muted)]">
              in {mailboxName}
            </span>
          )}
        </div>
      </div>

      <div className="px-4 py-4 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="font-semibold text-[var(--color-text)]">
              {email.from?.[0]?.name || email.from?.[0]?.email || 'Unknown'}
            </div>
            <div className="text-sm text-[var(--color-text-secondary)]">
              {email.from?.map((f) => f.email).join(', ')}
            </div>
          </div>
          <span className="text-sm text-[var(--color-text-muted)]">
            {email.receivedAt ? formatDate(email.receivedAt) : ''}
          </span>
        </div>

        <div className="text-sm text-[var(--color-text-secondary)]">
          <span className="text-[var(--color-text-muted)]">To:</span>{' '}
          {email.to?.map((t) => t.name || t.email).join(', ') || 'Unknown'}
        </div>

        {email.cc && email.cc.length > 0 && (
          <div className="text-sm text-[var(--color-text-secondary)]">
            <span className="text-[var(--color-text-muted)]">Cc:</span>{' '}
            {email.cc.map((c) => c.name || c.email).join(', ')}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {hasBody ? (
          bodyHtml ? (
            <div
              className="prose prose-sm max-w-none text-[var(--color-text)]"
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
          ) : (
            <pre className="whitespace-pre-wrap font-sans text-sm text-[var(--color-text)]">
              {bodyText}
            </pre>
          )
        ) : (
          <div className="text-[var(--color-text-muted)] italic">
            {email.preview || '(no content)'}
          </div>
        )}
      </div>

      {email.attachments && email.attachments.length > 0 && (
        <div className="px-4 py-3 border-t border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
          <div className="text-sm font-medium text-[var(--color-text-secondary)] mb-2">
            Attachments ({email.attachments.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {email.attachments.map((att) => (
              <div
                key={att.id}
                className="px-3 py-2 border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text-secondary)]"
              >
                {att.name} ({Math.round(att.size / 1024)}KB)
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
